import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Pagination,
  Chip,
  Stack,
  Paper,
  Divider,
  Skeleton,
  MenuItem,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CustomCard from "../components/CustomCard";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import TranslatedJobCard from "../components/TranslatedJobCard";
import Categories from "../components/Categories";
import AiSearch from "../components/AiSearch";
import { useCategory } from "../contexts/CategoryContext";
import { apiUrl } from "../config/api";
import { getCurrentLang } from "../components/LangSelector";
import useSEO from "../hooks/useSEO";

const JobList = () => {
  const [cardsData, setCardsData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);

  const { selectedCategory } = useCategory();

  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;
  const scrollRestoredRef = useRef(false);

  // SEO — динамический заголовок
  const seoParams = new URLSearchParams(location.search);
  const seoQ = seoParams.get("q");
  const seoCity = seoParams.get("city");
  let seoTitle = 'JobMD - Работа в Молдове';
  if (seoQ) seoTitle = `Поиск: ${seoQ} - JobMD`;
  else if (selectedCategory) seoTitle = `${selectedCategory} - Работа в Молдове | JobMD`;
  else if (seoCity) seoTitle = `Вакансии в ${seoCity} - JobMD`;

  useSEO({ title: seoTitle });

  const fetchData = useCallback(async () => {
    setLoading(true);

    const params = new URLSearchParams(location.search);

    const page = parseInt(params.get("page") || "1", 10);
    const q = params.get("q");
    const city = params.get("city");
    const min = params.get("min");
    const max = params.get("max");
    const sort = params.get("sort") || "new";

    const lang = getCurrentLang();

    let url = `${apiUrl("jobs.php")}?limit=${limit}&page=${page}&lang=${lang}`;

    if (selectedCategory) {
      url += `&category=${encodeURIComponent(selectedCategory)}`;
    }

    if (q) url += `&q=${encodeURIComponent(q)}`;
    if (city) url += `&city=${encodeURIComponent(city)}`;
    if (min) url += `&min=${min}`;
    if (max) url += `&max=${max}`;
    if (sort && sort !== "new") url += `&sort=${sort}`;

    console.log("🔍 JobList fetchData:", { url, page, lang, limit, selectedCategory, sort });

    try {
      const res = await fetch(url);
      const data = await res.json();

      if (data.jobs) {
        setCardsData(data.jobs);
        setTotalJobs(data.total || data.jobs.length);
        setTotalPages(Math.ceil((data.total || data.jobs.length) / limit));
      } else {
        setCardsData([]);
        setTotalJobs(0);
        setTotalPages(1);
      }
    } catch {
      setCardsData([]);
      setTotalJobs(0);
      setTotalPages(1);
    }

    setLoading(false);
  }, [selectedCategory, location.search]);

  // 🔥 Перезагрузка при смене языка
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('app:langChange', handler);
    return () => window.removeEventListener('app:langChange', handler);
  }, [fetchData]);

  // 🔥 Save scroll position when leaving to job details
  const handleCardClick = (jobId) => {
    sessionStorage.setItem("lastJobListScrollY", window.scrollY.toString());
    sessionStorage.setItem("lastJobListJobId", jobId.toString());
    
    // Добавляем класс для анимации перехода КОНТЕНТУ, не body
    const content = document.querySelector(".transition-content");
    if (content) {
      content.classList.add("page-transition-content");
    }
    
    setTimeout(() => {
      navigate(`/job/${jobId}`);
    }, 300);
  };

  // 🔥 Restore scroll position when returning from job details
  useEffect(() => {
    if (scrollRestoredRef.current) return;

    const savedScrollY = sessionStorage.getItem("lastJobListScrollY");
    const savedJobId = sessionStorage.getItem("lastJobListJobId");

    if (savedScrollY && savedJobId && !loading && cardsData.length > 0) {
      // 🔥 CRITICAL FIX: Reset scroll to top first, then scroll to saved position
      // This ensures fixed navbar attaches correctly to top:0
      setTimeout(() => {
        const targetScrollY = parseInt(savedScrollY, 10);
        
        // 1. First, instantly scroll to top (navbar will attach to top:0)
        window.scrollTo({ top: 0, behavior: "instant" });
        
        // 2. Wait one frame for navbar to settle
        requestAnimationFrame(() => {
          // 3. Now smooth scroll to saved position
          window.scrollTo({ 
            top: targetScrollY, 
            behavior: "smooth" 
          });
          
          // 4. Clean up after scroll completes
          setTimeout(() => {
            sessionStorage.removeItem("lastJobListScrollY");
            sessionStorage.removeItem("lastJobListJobId");
          }, 500);
        });
      }, 100);

      scrollRestoredRef.current = true;
    }
  }, [loading, cardsData]);

  // Reset restoration flag when location changes (new search/filter)
  useEffect(() => {
    scrollRestoredRef.current = false;
  }, [location.search]);

  useEffect(() => {
    localStorage.setItem(
      "lastJobListUrl",
      `${location.pathname}${location.search}`
    );
  }, [location.pathname, location.search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (event, page) => {
    const params = new URLSearchParams(location.search);
    params.set("page", page.toString());

    navigate(`/?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // сортировка живёт в URL (?sort=...), поэтому переживает переходы и «назад»
  const sort = new URLSearchParams(location.search).get("sort") || "new";

  const handleSortChange = (e) => {
    const params = new URLSearchParams(location.search);
    params.delete("page"); // возврат в начало списка при смене сортировки
    if (e.target.value === "new") params.delete("sort");
    else params.set("sort", e.target.value);
    navigate(`/?${params.toString()}`);
  };

  const handleSortReset = () => {
    const params = new URLSearchParams(location.search);
    params.delete("sort");
    params.delete("page");
    navigate(`/?${params.toString()}`);
  };

  const currentPage =
    parseInt(new URLSearchParams(location.search).get("page") || "1", 10) || 1;

  return (
    <Box>

      {/* 🔥 HERO */}
      <Box
        mb={3}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: { xs: 2, md: 2.5 },
          background:
            "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(236,72,153,0.08))",
          border: "1px solid rgba(99,102,241,0.08)",
          transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          animation: "fadeInUp 0.6s ease-out",
          "@keyframes fadeInUp": {
            "0%": {
              opacity: 0,
              transform: "translateY(10px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: "1.6rem", md: "2rem" } }}>
          Найди работу мечты 🚀
        </Typography>

        <Typography color="text.secondary" mb={2}>
          Более {totalJobs} вакансий уже ждут тебя
        </Typography>

        {/* ✨ Умный поиск с ИИ */}
        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: 2.5,
            background: "linear-gradient(135deg, rgba(99,102,241,0.10), rgba(236,72,153,0.07))",
            border: "1px solid rgba(99,102,241,0.14)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <AutoAwesomeIcon sx={{ color: "#6366f1", fontSize: 20 }} />
            <Typography fontWeight={700} fontSize={{ xs: "0.95rem", sm: "1rem" }}>
              Умный поиск с ИИ
            </Typography>
            <Typography variant="caption" color="text.secondary" display={{ xs: "none", sm: "inline" }}>
              Опишите работу своими словами — нейросеть подберёт вакансии
            </Typography>
          </Stack>
          <AiSearch />
        </Box>

        {/* FILTER INFO */}
        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
          {selectedCategory && (
            <Chip label={`Категория: ${selectedCategory}`} />
          )}
        </Stack>
      </Box>

      <Grid container spacing={{ xs: 0, md: 3 }} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 8.5, lg: 9 }}>
          <Paper
            className="slide-up"
            sx={{
              mb: 3,
              p: { xs: 1.5, md: 1.75 },
              borderRadius: 2,
              background: "rgba(255,255,255,0.75)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(15,23,42,0.07)",
              boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                boxShadow: "0 14px 36px rgba(15,23,42,0.08)",
                borderColor: "rgba(99,102,241,0.12)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {selectedCategory ? (
                  <Chip label={`Категория: ${selectedCategory}`} color="primary" />
                ) : (
                  <Chip label={`Все вакансии`} variant="outlined" />
                )}
                <Chip label={`${totalJobs} вакансий`} variant="outlined" />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  select
                  size="small"
                  value={sort}
                  onChange={handleSortChange}
                  sx={{
                    minWidth: { xs: "100%", sm: 220 },
                    // подсветка, когда сортировка включена (не по умолчанию)
                    ...(sort !== "new"
                      ? {
                          bgcolor: "rgba(99,102,241,0.12)",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6366f1",
                          },
                        }
                      : {}),
                  }}
                >
                  <MenuItem value="new">Сортировка: новые</MenuItem>
                  <MenuItem value="salary_desc">ЗП ↓ (сначала высокая)</MenuItem>
                  <MenuItem value="salary_asc">ЗП ↑ (сначала низкая)</MenuItem>
                </TextField>
                {sort !== "new" && (
                  <Button
                    size="small"
                    color="inherit"
                    title="Сбросить сортировку (по умолчанию)"
                    onClick={handleSortReset}
                    sx={{ minWidth: 0, p: 1 }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          {loading ? (
            <Stack spacing={2}>
              {[...Array(6)].map((_, i) => (
                <Paper key={i} sx={{ p: 2, borderRadius: 3 }}>
                  <Skeleton height={30} width="40%" />
                  <Skeleton />
                  <Skeleton width="60%" />
                </Paper>
              ))}
            </Stack>
          ) : cardsData.length > 0 ? (
            <>
              <Stack spacing={2}>
                {cardsData.map((job, index) => (
                  <Box key={job.id}>
                    <TranslatedJobCard
                      job={job}
                      onMoreClick={handleCardClick}
                    />

                    {index !== cardsData.length - 1 && (
                      <Divider sx={{ opacity: 0.3 }} />
                    )}
                  </Box>
                ))}
              </Stack>

              {totalPages > 1 && (
                <Box
                  mt={5}
                  mb={{ xs: 2, md: 0 }}
                  display="flex"
                  justifyContent="center"
                >
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    shape="rounded"
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontSize: { xs: "0.8rem", md: "0.875rem" },
                        minWidth: { xs: 32, md: 36 },
                        height: { xs: 32, md: 36 },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2.5 }}>
              <Typography variant="h6" mb={1}>
                😔 Ничего не найдено
              </Typography>

              <Typography color="text.secondary" mb={2}>
                Попробуй изменить фильтры или поиск
              </Typography>

              <Typography fontWeight="bold" mb={2}>
                🔥 Популярные вакансии:
              </Typography>

              <Stack spacing={2}>
                {[
                  "Frontend разработчик",
                  "Менеджер по продажам",
                  "Водитель категории B",
                ].map((job, i) => (
                  <Paper key={i} sx={{ p: 2, borderRadius: 3 }}>
                    {job}
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 3.5, lg: 3 }} sx={{ display: { xs: "none", md: "block" }, alignSelf: "flex-start" }}>
          <Paper
            className="slide-in-right"
            sx={{
              position: "sticky",
              top: 96,
              p: 1.5,
              borderRadius: 2,
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 16px 40px rgba(15,23,42,0.08)",
              zIndex: 10,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              willChange: "transform, box-shadow",
              "&:hover": {
                boxShadow: "0 20px 50px rgba(15,23,42,0.12)",
                borderColor: "rgba(99,102,241,0.15)",
              },
            }}
          >
            <Categories variant="sidebar" title="Категории" />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default JobList;